/**
 * Parsing for the duration grammar the timer field accepts. Extracted from
 * DurationInput so the timer panel, the per-entry inline edit and the input itself
 * all read the same rules — the grammar is unchanged from the old duration field.
 */

/**
 * Parse a clock reading into minutes: "01:33", or "10:00:20" with seconds, which is how
 * the panel renders every entry. Seconds come back as a fraction of a minute, so
 * minutesToSeconds round-trips the typed value exactly.
 */
export function parseTimeFormat(input: string): number | null {
    // Hours are unbounded (a tracked total outgrows two digits); minutes and seconds
    // are 0-59, so "1:75" stays a parse failure rather than becoming 2h15m.
    const timeRegex = /^(\d{1,4}):([0-5]?\d)(?::([0-5]?\d))?$/;
    const match = input.match(timeRegex);

    if (!match) return null;

    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    const seconds = match[3] ? Number.parseInt(match[3], 10) : 0;

    return hours * 60 + minutes + seconds / SECONDS_PER_MINUTE;
}

/**
 * Parse "1d 3h 30m" into minutes (decimals allowed, e.g. "5.5d 30m").
 * A bare number with no suffix is read as hours.
 */
export function parseDurationFormat(input: string): number | null {
    // Normalize whitespace but preserve single spaces
    const cleanInput = input.toLowerCase().trim();

    // Match days, hours, and minutes (including decimals)
    // Allow optional spaces between number and unit, and between different parts
    const dayMatch = cleanInput.match(/(\d+(?:\.\d+)?)\s*d/);
    const hourMatch = cleanInput.match(/(\d+(?:\.\d+)?)\s*h/);
    const minuteMatch = cleanInput.match(/(\d+(?:\.\d+)?)\s*m/);

    // Check if there's a standalone number without any suffix (treat as hours)
    // This should only match if there are no unit suffixes (d, h, m) in the input
    const hasAnySuffix = dayMatch || hourMatch || minuteMatch;
    const standaloneNumberMatch = !hasAnySuffix ? cleanInput.match(/^(\d+(?:\.\d+)?)$/) : null;

    // If no matches found, return null
    if (!dayMatch && !hourMatch && !minuteMatch && !standaloneNumberMatch) return null;

    const days = dayMatch ? Number.parseFloat(dayMatch[1]) : 0;
    const hours = hourMatch
        ? Number.parseFloat(hourMatch[1])
        : standaloneNumberMatch
          ? Number.parseFloat(standaloneNumberMatch[1])
          : 0;
    const minutes = minuteMatch ? Number.parseFloat(minuteMatch[1]) : 0;

    if (
        days < 0 ||
        hours < 0 ||
        minutes < 0 ||
        !Number.isFinite(days) ||
        !Number.isFinite(hours) ||
        !Number.isFinite(minutes)
    )
        return null;

    return Math.round(days * 24 * 60 + hours * 60 + minutes);
}

/** Parse any accepted format into minutes. Empty is 0; unparseable is null. */
export function parseDurationInput(input: string): number | null {
    if (!input.trim()) return 0;

    // Try time format first (01:33)
    const timeResult = parseTimeFormat(input.trim());
    if (timeResult !== null) return timeResult;

    // Try duration format (1d 3h 30m)
    const durationResult = parseDurationFormat(input.trim());
    if (durationResult !== null) return durationResult;

    return null;
}

export const SECONDS_PER_MINUTE = 60;

export const minutesToSeconds = (minutes: number) => Math.round(minutes * 60);
/** Floored to match the server: a partial minute is never rounded up into a whole one. */
export const secondsToMinutes = (seconds: number) => Math.floor(seconds / 60);
